class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('facet-remove a').forEach((element) => {
      element.classList.toggle('disabled', disable);

      if (disable) {
        if (!Object.prototype.hasOwnProperty.call(element.dataset, 'facetPreviousTabindex')) {
          element.dataset.facetPreviousTabindex = element.getAttribute('tabindex') || '';
        }
        element.setAttribute('aria-disabled', 'true');
        element.setAttribute('tabindex', '-1');
      } else {
        element.removeAttribute('aria-disabled');
        if (Object.prototype.hasOwnProperty.call(element.dataset, 'facetPreviousTabindex')) {
          if (element.dataset.facetPreviousTabindex) {
            element.setAttribute('tabindex', element.dataset.facetPreviousTabindex);
          } else {
            element.removeAttribute('tabindex');
          }
          delete element.dataset.facetPreviousTabindex;
        }
      }
    });
  }

  static captureFocusTarget(event) {
    let element = event?.target instanceof HTMLElement ? event.target : null;
    if (!event) {
      element = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (!element?.closest('facet-filters-form, facet-remove, .mobile-facets__wrapper')) return null;
    }
    if (element && !element.matches('a, button, input, select, textarea, [tabindex]')) {
      element = element.closest('a, button, input, select, textarea, [tabindex]');
    }
    if (!element && event?.currentTarget instanceof HTMLElement) element = event.currentTarget;
    if (!element) return null;

    return {
      element,
      id: element.id || '',
      name: element.getAttribute('name') || '',
      value: 'value' in element ? String(element.value) : element.getAttribute('value') || '',
      filterId: element.closest('.js-filter')?.id || '',
      facetRemoveIndex: Array.from(document.querySelectorAll('facet-remove a')).indexOf(element),
    };
  }

  static focusElement(element) {
    if (!(element instanceof HTMLElement) || !element.isConnected) return false;
    if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') return false;
    if (element.hidden || element.closest('[hidden]')) return false;
    if (!element.getClientRects().length) return false;
    if (element.offsetParent === null && window.getComputedStyle(element).position !== 'fixed') return false;

    element.focus();
    return document.activeElement === element;
  }

  static getVisibleFilterFallback() {
    const candidates = document.querySelectorAll(
      '.mobile-facets__open-wrapper, #FacetFiltersForm .facets__summary, #FacetFiltersPillsForm .facets__summary'
    );

    return Array.from(candidates).find(
      (element) =>
        element instanceof HTMLElement &&
        element.isConnected &&
        !element.hidden &&
        !element.closest('[hidden]') &&
        element.getClientRects().length > 0 &&
        (element.offsetParent !== null || window.getComputedStyle(element).position === 'fixed')
    );
  }

  static getFocusFallback(focusTarget) {
    if (!focusTarget?.filterId) return null;

    const filter = document.getElementById(focusTarget.filterId);
    if (!filter) return null;
    return filter.querySelector(
      filter.classList.contains('mobile-facets__details') ? '.mobile-facets__close-button' : '.facets__summary'
    );
  }

  static restoreFocus(focusTarget, fallback = null) {
    if (!focusTarget) return FacetFiltersForm.focusElement(fallback);

    if (FacetFiltersForm.focusElement(focusTarget.element)) return true;

    if (focusTarget.id && FacetFiltersForm.focusElement(document.getElementById(focusTarget.id))) return true;

    if (focusTarget.name) {
      const scope = focusTarget.filterId ? document.getElementById(focusTarget.filterId) : document;
      const matchingControl = Array.from(scope?.querySelectorAll('[name]') || []).find(
        (control) =>
          control.getAttribute('name') === focusTarget.name &&
          ('value' in control ? String(control.value) : control.getAttribute('value') || '') === focusTarget.value
      );
      if (FacetFiltersForm.focusElement(matchingControl)) return true;
    }

    if (focusTarget.facetRemoveIndex >= 0) {
      const facetRemoveLinks = Array.from(document.querySelectorAll('facet-remove a'));
      const nextFacetRemove = facetRemoveLinks[Math.min(focusTarget.facetRemoveIndex, facetRemoveLinks.length - 1)];
      if (FacetFiltersForm.focusElement(nextFacetRemove)) return true;
    }

    const fallbackElements = [
      fallback,
      FacetFiltersForm.getFocusFallback(focusTarget),
      FacetFiltersForm.getVisibleFilterFallback(),
    ];

    return fallbackElements.some((element) => FacetFiltersForm.focusElement(element));
  }

  static clearError() {
    document.querySelectorAll('[data-facet-error-status]').forEach((status) => status.remove());
  }

  static setLoadingState(loading) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const productGrid = productGridContainer?.querySelector('.collection');
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    productGrid?.classList.toggle('loading', loading);
    if (productGridContainer) productGridContainer.setAttribute('aria-busy', loading ? 'true' : 'false');
    countContainer?.classList.toggle('loading', loading);
    countContainerDesktop?.classList.toggle('loading', loading);
    loadingSpinners.forEach((spinner) => spinner.classList.toggle('hidden', !loading));
  }

  static handleRenderError(error, focusTarget) {
    console.error(error);
    FacetFiltersForm.setLoadingState(false);
    FacetFiltersForm.toggleActiveFacets(false);
    FacetFiltersForm.clearError();

    const statusContainers = Array.from(document.querySelectorAll('[role="status"]')).filter((status) =>
      status.querySelector('#ProductCount, #ProductCountDesktop')
    );
    const statusContainer = statusContainers.find((status) => status.getClientRects().length > 0) || statusContainers[0];
    const errorText = document.querySelector('[data-facet-error-text]')?.dataset.facetErrorText;
    if (statusContainer && errorText) {
      const errorStatus = document.createElement('span');
      errorStatus.className = 'visually-hidden';
      errorStatus.dataset.facetErrorStatus = '';
      errorStatus.setAttribute('role', 'alert');
      errorStatus.setAttribute('aria-atomic', 'true');
      errorStatus.textContent = errorText;
      statusContainer.appendChild(errorStatus);
    }

    window.requestAnimationFrame(() => {
      FacetFiltersForm.restoreFocus(focusTarget, FacetFiltersForm.getFocusFallback(focusTarget));
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const requestId = ++FacetFiltersForm.renderRequestId;
    FacetFiltersForm.requestController?.abort();
    FacetFiltersForm.requestController = new AbortController();
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const focusTarget = FacetFiltersForm.captureFocusTarget(event);
    FacetFiltersForm.clearError();
    FacetFiltersForm.toggleActiveFacets(true);
    FacetFiltersForm.setLoadingState(true);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      try {
        FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, focusTarget, requestId)
          : FacetFiltersForm.renderSectionFromFetch(
              url,
              event,
              focusTarget,
              requestId,
              FacetFiltersForm.requestController.signal
            );
      } catch (error) {
        if (requestId === FacetFiltersForm.renderRequestId) FacetFiltersForm.handleRenderError(error, focusTarget);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, focusTarget, requestId, signal) {
    fetch(url, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Facet request failed with status ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.renderRequestId) return;
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event, focusTarget);
        const productGridContainer = FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function' && productGridContainer) {
          initializeScrollAnimationTrigger(productGridContainer);
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || requestId !== FacetFiltersForm.renderRequestId) return;
        FacetFiltersForm.handleRenderError(error, focusTarget);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, focusTarget, requestId) {
    if (requestId !== FacetFiltersForm.renderRequestId) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event, focusTarget);
    const productGridContainer = FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function' && productGridContainer) {
      initializeScrollAnimationTrigger(productGridContainer);
    }
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    productGridContainer.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    productGridContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    return productGridContainer;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    FacetFiltersForm.setLoadingState(false);
  }

  static renderFilters(html, event, focusTarget = FacetFiltersForm.captureFocusTarget(event)) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => Boolean(focusTarget?.filterId && element.id === focusTarget.filterId);

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    let facetFallback = null;
    if (countsToRender) {
      const closestJSFilterID = focusTarget?.filterId || event?.target?.closest('.js-filter')?.id;
      const currentFacet = closestJSFilterID ? document.getElementById(closestJSFilterID) : null;

      if (currentFacet) {
        FacetFiltersForm.renderCounts(countsToRender, currentFacet);
        FacetFiltersForm.renderMobileCounts(countsToRender, currentFacet);

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        facetFallback = newFacetDetailsElement.querySelector(newElementSelector);
      }
    }

    if (focusTarget) {
      FacetFiltersForm.restoreFocus(focusTarget, facetFallback || FacetFiltersForm.getVisibleFilterFallback());
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const submittedForm = event.target.closest('form');
    if (submittedForm?.id === 'FacetSortDrawerForm') {
      const mobileFilterForm = document.getElementById('FacetFiltersFormMobile');
      if (mobileFilterForm) {
        const drawerParams = new URLSearchParams(this.createSearchParams(mobileFilterForm));
        Array.from(drawerParams.entries()).forEach(([key, value]) => {
          if (!value) drawerParams.delete(key);
        });
        drawerParams.set('sort_by', submittedForm.elements.sort_by.value);
        this.onSubmitForm(drawerParams.toString(), event);
        return;
      }
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;
    FacetFiltersForm.toggleActiveFacets();
    const targetUrl = new URL(event.currentTarget.href, window.location.origin);
    if (targetUrl.pathname !== window.location.pathname) {
      const currentParams = new URLSearchParams(window.location.search);
      ['_ab', '_fd', '_sc', 'preview_theme_id'].forEach((key) => {
        const value = currentParams.get(key);
        if (value !== null && !targetUrl.searchParams.has(key)) targetUrl.searchParams.set(key, value);
      });
      window.location.assign(targetUrl.toString());
      return;
    }
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.renderRequestId = 0;
FacetFiltersForm.requestController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.ctrlKey || event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

document.addEventListener('click', (event) => {
  const eventTarget = event.target instanceof Element ? event.target : null;
  const link = eventTarget?.closest('a[data-hf-preserve-query]');
  if (!link) return;

  const targetUrl = new URL(link.href, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);
  currentParams.delete('page');
  currentParams.delete('section_id');
  targetUrl.search = currentParams.toString();
  link.href = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
});
